import type { ServiceConfig } from '../types/auth';
import type { LoginProof } from '../types/identity';

/**
 * Messages pour la communication iframe avec Channel Messages.
 */
export interface ChannelMessage {
  type:
    | 'auth-request'
    | 'auth-response'
    | 'login-proof'
    | 'service-status'
    | 'error'
    | 'contract'
    | 'pairing-relay-status'
    | 'navigate-login';
  payload?: unknown;
}

export interface PairingRelayStatusMessage extends ChannelMessage {
  type: 'pairing-relay-status';
  payload: {
    pairingSatisfied: boolean;
    relayOk: boolean;
  };
}

export interface AuthRequestMessage extends ChannelMessage {
  type: 'auth-request';
  payload: {
    serviceId: string;
  };
}

export interface AuthResponseMessage extends ChannelMessage {
  type: 'auth-response';
  payload: {
    signature: string;
    publicKey: string;
    message: string;
  };
}

export interface LoginProofMessage extends ChannelMessage {
  type: 'login-proof';
  payload: LoginProof;
}

export interface ServiceStatusMessage extends ChannelMessage {
  type: 'service-status';
  payload: {
    services: ServiceConfig[];
  };
}

export interface ErrorMessage extends ChannelMessage {
  type: 'error';
  payload: {
    message: string;
    code?: string;
  };
}

export interface ContractMessage extends ChannelMessage {
  type: 'contract';
  payload: {
    contrat?: unknown;
    contrats_fils?: unknown[];
    actions?: unknown[];
  };
}

const FORBIDDEN_PAYLOAD_KEYS = ['privateKey', 'cle_privee'] as const;

/**
 * Recursively check that no secret keys (privateKey, cle_privee) are present
 * in a postMessage-to-parent payload. Throws if any forbidden key is found.
 * Use for any postMessage to parent (iframeChannel, iframe.ts). Key isolation.
 */
export function assertNoSecretsInChannelPayload(
  value: unknown,
  path = 'payload',
): void {
  if (value === null || value === undefined) {
    return;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    for (const k of Object.keys(obj)) {
      if (FORBIDDEN_PAYLOAD_KEYS.includes(k as (typeof FORBIDDEN_PAYLOAD_KEYS)[number])) {
        throw new Error(
          `[iframeChannel] Forbidden key "${k}" in channel ${path}: no secrets may leave iframe`,
        );
      }
      assertNoSecretsInChannelPayload(obj[k], `${path}.${k}`);
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, i) =>
      assertNoSecretsInChannelPayload(item, `${path}[${i}]`),
    );
  }
}

/**
 * Send message to parent window (Channel Messages).
 * Ensures no private keys or secrets leave the iframe (key isolation).
 */
export function sendToChannel(message: ChannelMessage): void {
  if (window.parent === window) {
    console.warn('Not in iframe context, message not sent');
    return;
  }
  assertNoSecretsInChannelPayload(message.payload);
  window.parent.postMessage(message, '*');
}

/**
 * Listen for messages from parent (Channel Messages).
 */
export function listenToChannel(
  handler: (message: ChannelMessage) => void,
): () => void {
  const messageHandler = (event: MessageEvent<ChannelMessage>): void => {
    if (event.data.type !== undefined) {
      handler(event.data);
    }
  };

  window.addEventListener('message', messageHandler);
  return () => {
    window.removeEventListener('message', messageHandler);
  };
}

/**
 * Check if running in iframe.
 */
export function isInIframe(): boolean {
  return window.parent !== window;
}
